import { useId } from 'react'
import { MapPin } from 'lucide-react'

import { Badge } from 'components/ui/badge'
import { OSM_TILE_URL } from 'features/Map/config/mapConfig'
import { cn } from 'lib/utils'
import type { AiPlanCandidate, AiPlace, AiRefInfo } from 'services/aiService'

import { placeAddress } from '../plan/planSteps'

/**
 * Choosing between what a resolver found (MYD-623): which Safeway, which John.
 *
 * Native radio buttons, so the arrow keys move between choices and the
 * choice is announced, with no extra keyboard handling. Picking one saves it
 * at once: there is nothing to confirm about a choice that is already a list
 * of the resolver's own answers.
 *
 * A place shows a map thumbnail and where it came from; "use the existing
 * location" choices sit with them when the user already has a location there.
 */

const SOURCE_LABELS: Record<AiPlace['source'], string> = {
	overture: 'Overture',
	photon: 'Photon',
	nominatim: 'Nominatim',
}

const SOURCE_HINTS: Record<AiPlace['source'], string> = {
	overture: 'From the Overture places dataset',
	photon: 'From an address search, not confirmed',
	nominatim: 'From an address search, confirmed',
}

export function SourceBadge({ source }: { source: AiPlace['source'] }) {
	return (
		<Badge variant={source === 'photon' ? 'warning' : 'secondary'} className="px-1.5 py-0 text-[10px] font-medium" title={SOURCE_HINTS[source]}>
			{SOURCE_LABELS[source]}
		</Badge>
	)
}

const TILE_ZOOM = 16

/**
 * One map tile around a point, with a pin where the point falls: enough to
 * tell two Safeways apart, without starting a map. Decorative, since the
 * address beside it says the same thing in words.
 */
export function MapThumb({ latitude, longitude }: { latitude: number; longitude: number }) {
	if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
	const scale = 2 ** TILE_ZOOM
	const xFloat = ((longitude + 180) / 360) * scale
	const latRad = (latitude * Math.PI) / 180
	const yFloat = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * scale
	const x = Math.floor(xFloat)
	const y = Math.floor(yFloat)
	const src = OSM_TILE_URL.replace('{z}', String(TILE_ZOOM)).replace('{x}', String(x)).replace('{y}', String(y))
	return (
		<span className="relative block h-14 w-14 shrink-0 overflow-hidden rounded border border-gray-200 bg-gray-100" aria-hidden="true">
			<img src={src} alt="" loading="lazy" className="h-14 w-14 object-cover" />
			<MapPin
				className="absolute h-4 w-4 -translate-x-1/2 -translate-y-full fill-primary text-white"
				style={{ left: `${(xFloat - x) * 100}%`, top: `${(yFloat - y) * 100}%` }}
			/>
		</span>
	)
}

export interface CandidatePickerProps {
	/** What is being chosen, e.g. "Which place?". */
	legend: string
	options: AiPlanCandidate[]
	/** The ref currently in the step's args. */
	value?: string
	refs: Record<string, AiRefInfo>
	/** Locations the user already has at one of these places. */
	existing?: AiPlanCandidate[]
	disabled?: boolean
	onPick: (ref: string) => void
	onUseExisting?: (locationRef: string) => void
}

function PlaceOption({ place, label }: { place?: AiPlace; label: string }) {
	if (!place) return <span className="text-sm text-gray-800">{label}</span>
	return (
		<span className="flex min-w-0 items-center gap-2.5">
			<MapThumb latitude={place.latitude} longitude={place.longitude} />
			<span className="min-w-0">
				<span className="flex items-center gap-1.5">
					<span className="truncate text-sm font-medium text-gray-900">{place.name || place.address}</span>
					<SourceBadge source={place.source} />
				</span>
				<span className="block truncate text-xs text-gray-500">{placeAddress(place)}</span>
			</span>
		</span>
	)
}

export function CandidatePicker({ legend, options, value, refs, existing = [], disabled, onPick, onUseExisting }: CandidatePickerProps) {
	const name = useId()
	const row = (checked: boolean) =>
		cn(
			'flex cursor-pointer items-center gap-2.5 rounded-md border px-2.5 py-2 focus-within:ring-2 focus-within:ring-ring',
			checked ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300',
			disabled && 'cursor-not-allowed opacity-60',
		)

	return (
		<fieldset className="mt-2 space-y-1.5" disabled={disabled}>
			<legend className="mb-1 text-xs font-medium text-gray-600">{legend}</legend>
			{existing.map(option => (
				<label key={option.ref} className={row(false)}>
					<input type="radio" name={name} className="h-4 w-4 accent-primary" onChange={() => onUseExisting?.(option.ref)} />
					<span className="min-w-0 text-sm">
						<span className="font-medium text-gray-900">Use your existing location</span>
						<span className="block truncate text-xs text-gray-500">{option.label}</span>
					</span>
				</label>
			))}
			{options.map(option => {
				const info = refs[option.ref]
				const checked = option.ref === value
				return (
					<label key={option.ref} className={row(checked)}>
						<input type="radio" name={name} checked={checked} className="h-4 w-4 accent-primary" onChange={() => onPick(option.ref)} />
						{info?.kind === 'place' ? <PlaceOption place={info.place} label={option.label} /> : <span className="text-sm text-gray-800">{info?.label || option.label}</span>}
					</label>
				)
			})}
		</fieldset>
	)
}

export default CandidatePicker
